import {
  CurrencyCode,
  enumCurrencyCode,
  enumUnit,
  enumWeightDisplayUnit,
  MoneyInput,
  Unit,
  Weight,
  WeightDisplayUnit,
  WeightInput,
} from '@rematter/frontend-lib-api-client';
import { convertFromPounds, weightToPounds } from '@rematter/ui-web';
import Big from 'big.js';

import {
  enumLoadWeightType,
  LoadFormLineItem,
  LoadLineItemRecord,
  WeightUnitType,
} from '../types/load';
import { PurchaseLoadLineItemRecord } from '../types/purchaseLoad';

import { toMoney, zeroMoney } from './money';

export const createDefaultWeights = (
  unit: Unit,
  values?: { gross?: number; tare?: number; net?: number },
) => ({
  [enumLoadWeightType.Gross]: {
    value: values?.gross ?? 0,
    unit,
    __typename: 'Weight' as const,
  },
  [enumLoadWeightType.Tare]: {
    value: values?.tare ?? 0,
    unit,
    __typename: 'Weight' as const,
  },
  [enumLoadWeightType.Net]: {
    value: values?.net ?? 0,
    unit,
    __typename: 'Weight' as const,
  },
});

/**
 * Creates a getFooterDisplayUnit function based on weight display settings.
 * This determines what unit to display in footer totals based on the toggle state.
 */
export const createGetFooterDisplayUnit =
  (
    weightUnit: WeightUnitType,
    facilityDefaultUnit: Unit,
  ): ((pricingUnit: Unit) => Unit) =>
  (pricingUnit: Unit): Unit => {
    // Exception: "Each" always displays as "Each" regardless of toggle
    if (pricingUnit === enumUnit.Each) return enumUnit.Each;

    // If scale mode, use facility default; otherwise use pricing unit
    return weightUnit === 'scale' ? facilityDefaultUnit : pricingUnit;
  };

/**
 * Returns a zero Weight value with Pounds unit
 */
export function zeroWeight(unit?: Unit): Weight {
  return {
    value: 0,
    unit: unit ?? (enumUnit.Pounds as Unit),
    __typename: 'Weight' as const,
  };
}

/**
 * Returns a zero WeightInput value with Pounds unit (for API inputs)
 */
export function zeroWeightInput(): WeightInput {
  return {
    value: 0,
    unit: enumUnit.Pounds as Unit,
  };
}

export const toWeight = (value: number, unit: Unit): Weight => ({
  value,
  unit,
  __typename: 'Weight' as const,
});

/**
 * Converts a Weight object to WeightInput (removes __typename for API calls)
 */
export function toWeightInput(
  weight: Pick<Weight, 'value' | 'unit'> | null | undefined,
): WeightInput | null {
  if (!weight || weight.value === null || weight.value === undefined) {
    return null;
  }

  // Explicitly destructure to exclude __typename and any other Apollo cache properties
  const { value, unit } = weight;

  return {
    value,
    unit,
  };
}

/**
 * Creates a WeightInput from partial Weight data (useful for form state)
 */
export function toWeightInputFromPartial(
  weight: Partial<Pick<Weight, 'value' | 'unit'>> | null | undefined,
): WeightInput {
  if (!weight || weight.value === null || weight.value === undefined) {
    return zeroWeightInput();
  }

  return {
    value: weight.value,
    unit: weight.unit || ('Pounds' as Unit),
  };
}

/**
 * Weight conversion factors to pounds
 */
const WEIGHT_TO_POUNDS_FACTOR: Record<string, number> = {
  Pounds: 1,
  Ounces: 1 / 16,
  NetTons: 2000, // 1 NetTon = 2000 pounds
  ShortTons: 2000, // Same as NetTons
  GrossTons: 2240, // 1 GrossTon = 2240 pounds
  LongTons: 2240, // Same as GrossTons
  Kilograms: 2.20462, // 1 kg = 2.20462 pounds
  Grams: 2.20462 / 1000, // 1 gram = 0.00220462 pounds
  MetricTons: 2204.62, // 1 metric ton = 2204.62 pounds
  ShortHundredweight: 100, // 1 short hundredweight = 100 pounds
  LongHundredweight: 112, // 1 long hundredweight = 112 pounds
} as const;

/**
 * Converts a sales order quantity with unit to a Weight object in Pounds
 * This is used when creating load line items from sales order line items
 * to ensure proper unit conversion (e.g., 25.5 NetTons → 51,000 Pounds)
 */
export function convertSalesOrderQuantityToWeight(
  quantity: number,
  unit: string,
): Weight {
  // Handle zero or undefined values
  if (!quantity || quantity === 0) {
    return zeroWeight();
  }

  if (unit === enumUnit.Each) {
    return {
      value: quantity,
      unit: enumUnit.Each,
      __typename: 'Weight' as const,
    };
  }

  // Get the conversion factor to pounds
  const conversionFactor = WEIGHT_TO_POUNDS_FACTOR[unit];
  if (!conversionFactor) {
    // If we don't know the unit, default to treating it as pounds
    console.warn(`Unknown weight unit: ${unit}, treating as Pounds`);
    return {
      value: quantity,
      unit: 'Pounds' as Unit,
      __typename: 'Weight' as const,
    };
  }

  // Convert to pounds
  const poundsValue = quantity * conversionFactor;

  return {
    value: poundsValue,
    unit: 'Pounds' as Unit,
    __typename: 'Weight' as const,
  };
}

/**
 * Converts weight from Pounds to the specified pricing unit for calculation purposes only
 * This is used to calculate prices when the pricing unit is different from the weight display unit
 * Example: 90,400 lb weight with NetTons pricing → 45.2 NT for calculation
 */
export function convertWeightForPricing(
  weightInPounds: number,
  targetPricingUnit: string,
): number {
  // Handle zero or undefined values
  if (!weightInPounds || weightInPounds === 0) {
    return 0;
  }

  // If target unit is Pounds, no conversion needed
  if (targetPricingUnit === 'Pounds') {
    return weightInPounds;
  }

  // Get the conversion factor from pounds to target unit
  const conversionFactor = WEIGHT_TO_POUNDS_FACTOR[targetPricingUnit];
  if (!conversionFactor) {
    // If we don't know the unit, treat as pounds (no conversion)
    console.warn(
      `Unknown pricing unit: ${targetPricingUnit}, treating as Pounds`,
    );
    return weightInPounds;
  }

  // Convert from pounds to target unit
  // Since WEIGHT_TO_POUNDS_FACTOR converts TO pounds, we need to divide to convert FROM pounds
  const convertedValue = weightInPounds / conversionFactor;

  return convertedValue;
}

// Helper function to convert Weight to display value in the pricing unit (same as SalesOrderLoadFooter)
export const convertWeightToDisplayValue = (
  weight: Weight | null,
  pricingUnit: Unit,
): number | null => {
  if (!weight || !weight.value || weight.value === 0) return null;

  // If the weight is already in the pricing unit, return it directly
  if (weight.unit === pricingUnit) {
    return weight.value;
  }

  // Convert weight to pounds first, then to the pricing unit
  const poundsValue = weightToPounds(weight);
  if (poundsValue === 0 || isNaN(poundsValue)) return null;

  // Convert pounds to the pricing unit directly
  const result = convertFromPounds(poundsValue, pricingUnit);
  return isNaN(result) ? null : result;
};

/**
 * Calculates the total value for a line item based on its net weight, unit price, and pricing unit
 * This is commonly used to calculate load line item totals across different components
 */
export const calculateLineItemsTotal = (
  lineItems: {
    weights?: Record<string, Weight> | null;
    pricingUnit?: string | null;
    unitPrice?: { amount: number } | null;
  }[],
) =>
  lineItems.reduce((sum, li) => {
    const netWeightPounds = weightToPounds(
      li.weights?.[enumLoadWeightType.Net],
    );
    const pricingUnit = li.pricingUnit ?? enumUnit.Pounds;
    const convertedWeight = convertWeightForPricing(
      netWeightPounds,
      pricingUnit,
    );
    return sum + (li.unitPrice?.amount ?? 0) * convertedWeight;
  }, 0);

/**
 * Calculates the remaining weight between requested and staged weights
 * Handles unit conversion when weights are in different units
 */
export function calculateRemainingWeight(
  requestedWeight: Weight | null | undefined,
  stagedWeight?: Weight | null,
): Weight {
  // Handle missing or invalid requested weight
  if (!requestedWeight?.value || !requestedWeight?.unit) {
    return {
      value: 0,
      unit: requestedWeight?.unit ?? enumUnit.Pounds,
      __typename: 'Weight' as const,
    };
  }

  const stagedValue = stagedWeight?.value ?? 0;

  // For "Each" unit, no conversion needed - just subtract directly
  if (requestedWeight.unit === enumUnit.Each) {
    const canSubtract = stagedWeight?.unit === enumUnit.Each;
    const remainingValue = canSubtract
      ? requestedWeight.value - stagedValue
      : requestedWeight.value;

    return {
      value: remainingValue,
      unit: requestedWeight.unit,
      __typename: 'Weight' as const,
    };
  }

  // For weight units, ensure both are in the same unit before subtracting
  // If units don't match, convert both to pounds for calculation
  if (stagedWeight?.unit && stagedWeight.unit !== requestedWeight.unit) {
    const requestedInPounds = weightToPounds(requestedWeight);
    const stagedInPounds = weightToPounds(stagedWeight);
    const remainingInPounds = requestedInPounds - stagedInPounds;
    const remainingValue = convertFromPounds(
      remainingInPounds,
      requestedWeight.unit as Unit,
    );

    return {
      value: remainingValue,
      unit: requestedWeight.unit,
      __typename: 'Weight' as const,
    };
  }

  // Units match, can subtract directly
  return {
    value: requestedWeight.value - stagedValue,
    unit: requestedWeight.unit,
    __typename: 'Weight' as const,
  };
}

/**
 * Checks if there is remaining weight between requested and staged amounts
 * Returns true if staged weight is less than requested weight
 * Handles unit conversion when weights are in different units
 */
export function hasRemainingWeight(
  requestedWeight: Weight | null | undefined,
  stagedWeight?: Weight | null,
): boolean {
  // If no requested weight, consider as having remaining (don't filter out)
  if (!requestedWeight?.value || !requestedWeight?.unit) {
    return true;
  }

  const stagedValue = stagedWeight?.value ?? 0;

  // For "Each" unit, compare directly
  if (requestedWeight.unit === enumUnit.Each) {
    return stagedWeight?.unit === enumUnit.Each
      ? stagedValue < requestedWeight.value
      : true;
  }

  // For weight units, convert to common unit if they don't match
  if (stagedWeight?.unit && stagedWeight.unit !== requestedWeight.unit) {
    const requestedInPounds = weightToPounds(requestedWeight);
    const stagedInPounds = weightToPounds(stagedWeight);
    return stagedInPounds < requestedInPounds;
  }

  // Units match, can compare directly
  return stagedValue < requestedWeight.value;
}

/**
 * Generic line item interface for calculating totals
 * Works with both LoadFormLineItem and sales order line items
 */
export type LineItemForTotals = {
  quantity: number | null | undefined;
  unit: string | null | undefined;
};

/**
 * Converts LoadFormLineItem to LineItemForTotals format
 * Uses weights[Net]?.value as quantity and weights[Net]?.unit as unit
 * Falls back to pricingUnit if weight unit is not available
 */
export function toLineItemForTotals(
  lineItem: LoadFormLineItem | LoadLineItemRecord,
): LineItemForTotals {
  return {
    quantity: lineItem.weights?.[enumLoadWeightType.Net]?.value ?? null,
    unit:
      lineItem.weights?.[enumLoadWeightType.Net]?.unit ??
      lineItem.pricingUnit ??
      null,
  };
}

/**
 * Checks if a line item is an "Each" item based on its pricing unit
 */
export function isEachItem(
  lineItem: LoadFormLineItem | LoadLineItemRecord,
): boolean {
  return lineItem.pricingUnit === enumUnit.Each;
}

/**
 * Determines the common pricing unit from a list of line items.
 * Returns the pricing unit if all weight-based items share the same unit,
 * otherwise returns the provided fallback unit.
 */
export function getCommonPricingUnit(
  lineItems: LineItemForTotals[],
  fallbackUnit: Unit = enumUnit.Pounds,
): Unit {
  // Filter to weight-based items only (exclude "Each")
  const weightItems = lineItems.filter(
    (item) => item.unit && item.unit !== enumUnit.Each,
  );

  if (weightItems.length === 0) {
    return fallbackUnit;
  }

  const firstUnit = weightItems[0]?.unit;
  const samePricingUnit = weightItems.every((item) => item.unit === firstUnit);

  return samePricingUnit && firstUnit ? (firstUnit as Unit) : fallbackUnit;
}

/**
 * Determines the common pricing unit from LoadFormLineItem array.
 * Returns the pricing unit if all weight-based items share the same unit,
 * otherwise returns the provided fallback unit.
 */
export function getCommonPricingUnitFromLoadItems(
  lineItems: (LoadFormLineItem | LoadLineItemRecord)[],
  fallbackUnit: Unit = enumUnit.Pounds,
): Unit {
  // Filter to weight-based items only (exclude "Each" based on pricingUnit)
  const weightItems = lineItems.filter((item) => !isEachItem(item));

  if (weightItems.length === 0) {
    return fallbackUnit;
  }

  const firstUnit = weightItems[0]?.pricingUnit;
  const samePricingUnit = weightItems.every(
    (item) => item.pricingUnit === firstUnit,
  );

  return samePricingUnit && firstUnit ? (firstUnit as Unit) : fallbackUnit;
}

/**
 * Calculates the total count of "Each" items from a list of line items
 */
export function calculateTotalEachCount(
  lineItems: LineItemForTotals[],
): number {
  return lineItems
    .reduce((acc, item) => {
      if (item.unit === enumUnit.Each) {
        return acc.plus(item.quantity ?? 0);
      }
      return acc;
    }, new Big(0))
    .toNumber();
}

/**
 * Calculates the total count of "Each" items from LoadFormLineItem array
 * Uses pricingUnit to determine if item is "Each" type
 */
export function calculateTotalEachCountFromLoadItems(
  lineItems: (LoadFormLineItem | LoadLineItemRecord)[],
): number {
  return lineItems
    .reduce((acc, item) => {
      if (isEachItem(item)) {
        return acc.plus(item.weights?.[enumLoadWeightType.Net]?.value || 0);
      }
      return acc;
    }, new Big(0))
    .toNumber();
}

/**
 * Calculates the total net weight from weight-based line items (excludes "Each" items)
 * Converts all weights to pounds, sums them, then converts to the target display unit
 */
export function calculateTotalNetWeight(
  lineItems: LineItemForTotals[],
  displayUnit: Unit = enumUnit.Pounds,
): Weight | null {
  // Filter to weight-based items only (exclude "Each")
  const weightItems = lineItems.filter(
    (item) => item.unit && item.unit !== enumUnit.Each,
  );

  if (weightItems.length === 0) {
    return null;
  }

  // Convert all weights to pounds and sum
  const totalInPounds = weightItems
    .reduce((acc, item) => {
      const quantity = new Big(item.quantity ?? 0);
      const unit = item.unit as string;

      // Get the conversion factor to pounds
      const conversionFactor = WEIGHT_TO_POUNDS_FACTOR[unit];
      if (!conversionFactor) {
        // If we don't know the unit, treat as pounds
        return acc.plus(quantity);
      }

      return acc.plus(quantity.times(conversionFactor));
    }, new Big(0))
    .toNumber();

  if (totalInPounds <= 0) {
    return null;
  }

  // Convert from pounds to the display unit
  const displayValue = convertFromPounds(totalInPounds, displayUnit);

  return {
    value: displayValue,
    unit: displayUnit,
    __typename: 'Weight' as const,
  };
}

/**
 * Calculates the total net weight from LoadFormLineItem array (excludes "Each" items)
 * Uses the weight's actual unit for conversion, not the pricingUnit
 */
export function calculateTotalNetWeightFromLoadItems(
  lineItems: (LoadFormLineItem | LoadLineItemRecord)[],
  displayUnit: Unit = enumUnit.Pounds,
): Weight | null {
  // Filter to weight-based items only (exclude "Each" based on pricingUnit)
  const weightItems = lineItems.filter((item) => !isEachItem(item));

  if (weightItems.length === 0) {
    return null;
  }

  // Convert all weights to pounds using the weight's actual unit, then sum
  const totalInPounds = weightItems.reduce((acc, item) => {
    const weight = item.weights?.[enumLoadWeightType.Net];
    if (!weight?.value) return acc;

    const poundsValue = weightToPounds(weight);
    return acc + poundsValue;
  }, 0);

  if (totalInPounds <= 0) {
    return null;
  }

  // Convert from pounds to the display unit
  const displayValue = convertFromPounds(totalInPounds, displayUnit);

  return {
    value: displayValue,
    unit: displayUnit,
    __typename: 'Weight' as const,
  };
}

type PartialSalesOrderLineItems = {
  quantity: number | null;
  unit: Unit | null;
  totalPrice: {
    amount: number;
    currencyCode: CurrencyCode;
  };
};

export const getSalesOrderLineItemsTotals = (
  lineItems: PartialSalesOrderLineItems[],
  weightDisplayUnit: WeightDisplayUnit,
) => {
  if (!lineItems) {
    return {
      totalEachCount: undefined,
      totalNetWeight: undefined,
      netWeight: undefined,
      totalPrice: zeroMoney(),
    };
  }

  // Convert line items to the format expected by the helpers
  const lineItemsForTotals = lineItems?.map((item) => ({
    quantity: item.quantity,
    unit: item.unit,
  }));

  // Determine display unit based on weightDisplayUnit preference
  // If PriceUnit mode, use the common pricing unit from line items
  // If ScaleUnit mode or not set, default to Pounds (as we don't have facility context)
  const pricingUnit = getCommonPricingUnit(lineItemsForTotals);
  const displayUnit =
    weightDisplayUnit === enumWeightDisplayUnit.PriceUnit
      ? pricingUnit
      : enumUnit.Pounds;

  // Calculate totals using reusable helpers
  const totalEachCount = calculateTotalEachCount(lineItemsForTotals);
  const totalNetWeight = calculateTotalNetWeight(
    lineItemsForTotals,
    displayUnit,
  );

  // Sum of line items in pounds
  const netWeight = weightToPounds(totalNetWeight);

  const totalPriceAmount = lineItems.reduce(
    (acc, item) => acc + (item.totalPrice?.amount || 0),
    0,
  );

  const currencyCode =
    lineItems?.[0]?.totalPrice?.currencyCode ?? enumCurrencyCode.USD;

  const totalPrice =
    lineItems.length > 0
      ? toMoney({
          amount: totalPriceAmount,
          currencyCode,
        })
      : zeroMoney();

  return {
    totalEachCount,
    totalNetWeight,
    netWeight,
    totalPrice,
  };
};

export type NormalizedLineItem = {
  id: string;
  itemName: string;
  type: string;
  weights: Record<string, Weight> | null;
  unitPrice: MoneyInput | null;
  pricingUnit: Unit;
  notes: string;
};

export function normalizeOutboundLineItem(
  li: LoadLineItemRecord,
): NormalizedLineItem {
  return {
    id: li.id,
    itemName: li.item?.name ?? '-',
    type: li.type ?? '-',
    weights: li.weights ?? null,
    unitPrice: li.unitPrice ?? null,
    pricingUnit: (li.pricingUnit ?? enumUnit.Pounds) as Unit,
    notes: li.notes ?? '',
  };
}

export function normalizeInboundLineItem(
  li: PurchaseLoadLineItemRecord,
): NormalizedLineItem {
  const weights: Record<string, Weight> = {};
  if (li.grossWeight) weights[enumLoadWeightType.Gross] = li.grossWeight;
  if (li.tareWeight) weights[enumLoadWeightType.Tare] = li.tareWeight;
  if (li.netWeight) weights[enumLoadWeightType.Net] = li.netWeight;

  return {
    id: li.id,
    itemName: li.item?.name ?? '-',
    type: li.type ?? '-',
    weights: Object.keys(weights).length > 0 ? weights : null,
    unitPrice: li.unitPrice ?? null,
    pricingUnit: li.pricingUnit ?? enumUnit.Pounds,
    notes: li.notes ?? '',
  };
}

export function calculateSingleLineTotal(
  item: Pick<NormalizedLineItem, 'weights' | 'unitPrice' | 'pricingUnit'>,
): MoneyInput | null {
  const net = item.weights?.[enumLoadWeightType.Net];
  const netLbs = weightToPounds(net);
  const netInPricing = convertWeightForPricing(netLbs, item.pricingUnit);
  const amount = (item.unitPrice?.amount ?? 0) * netInPricing;

  return amount > 0 && item.unitPrice
    ? { amount, currencyCode: item.unitPrice.currencyCode }
    : null;
}

export function calculateNormalizedLineTotals(items: NormalizedLineItem[]) {
  const weightItems = items.filter((i) => i.pricingUnit !== enumUnit.Each);
  const eachItems = items.filter((i) => i.pricingUnit === enumUnit.Each);

  let grossSum = 0;
  let tareSum = 0;
  let netSum = 0;
  let summaryUnit: Unit = enumUnit.Pounds;

  for (const item of weightItems) {
    const gross = item.weights?.[enumLoadWeightType.Gross];
    const tare = item.weights?.[enumLoadWeightType.Tare];
    const net = item.weights?.[enumLoadWeightType.Net];

    grossSum += Number(gross?.value ?? 0);
    tareSum += Number(tare?.value ?? 0);
    netSum += Number(net?.value ?? 0);

    if (summaryUnit === enumUnit.Pounds && (gross?.unit || net?.unit)) {
      summaryUnit = gross?.unit ?? net?.unit ?? enumUnit.Pounds;
    }
  }

  const eachCount = eachItems.reduce(
    (sum, item) =>
      sum + Number(item.weights?.[enumLoadWeightType.Net]?.value ?? 0),
    0,
  );

  const lineTotalSum = calculateLineItemsTotal(items);
  const currency = items.find((i) => i.unitPrice)?.unitPrice?.currencyCode;

  return {
    gross: grossSum > 0 ? toWeight(grossSum, summaryUnit) : null,
    tare: tareSum > 0 ? toWeight(tareSum, summaryUnit) : null,
    net: netSum > 0 ? toWeight(netSum, summaryUnit) : null,
    eachCount: eachCount > 0 ? eachCount : null,
    lineTotal:
      lineTotalSum > 0 && currency
        ? { amount: lineTotalSum, currencyCode: currency }
        : null,
  };
}
